import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	faCheck,
	faPen,
	faRotate,
	faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { TextArea } from "@/components/TextArea/TextArea";
import { AiModelUseCase, MediaJobType } from "@/pages/Private/redux/media/media.schema";
import { NarrationValidation, VideoWizard } from "@/pages/Private/redux/media/videoWizard.schema";
import { classNames } from "@/utils/classNames";

import { ModelSelectRow } from "../ModelSelectRow";
import { isStepApproved } from "../wizard.utils";

interface Step3NarrationProps {
	wizard: VideoWizard;
	isBusy: boolean;
	onSelectModel: (aiModelId: number) => void;
	/**
	 * Writes (or, with feedback, rewrites) the script — no audio is involved. The brief is
	 * the author's standing "what kind of script": it travels with every generate and the
	 * server keeps it, so retakes are steered the same way. Naming scenes rewrites only
	 * those — the per-scene iterate on each card.
	 */
	onGenerate: (feedback?: string, brief?: string, sceneIds?: number[]) => void;
	/** The author's own words for one scene — checked and reported on, never refused. */
	onEditSceneText: (sceneId: number, text: string) => void;
	onApprove: () => void;
}

/**
 * Step 3 — the words.
 *
 * Generate writes each scene's narration text through the governance pipeline; the feedback
 * box steers the next draft until the author likes what they read; approve is a plain
 * approve the server only accepts once every scene carries a text. The voice that will
 * speak it is the next step's business, which is exactly why iterating here is cheap — no
 * audio is synthesised until the words are settled.
 */
export const Step3Narration: FunctionComponent<Step3NarrationProps> = ({
	wizard,
	isBusy,
	onSelectModel,
	onGenerate,
	onEditSceneText,
	onApprove,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`videoWizard.${key}`, options ?? {}),
		[t]
	);

	const [feedback, setFeedback] = useState("");
	// The standing brief, prefilled from the session so a reload shows what still steers
	// the script. Deliberately not cleared after a generate — it is a setting, not a note.
	const [brief, setBrief] = useState(wizard.narrationBrief ?? "");

	const { scenes } = wizard.storyboard;
	const approved = isStepApproved(wizard, 3);

	// Only this step's own job spins here: the audio step shares the job type, so the
	// step number on the job is what tells the two apart.
	const scriptJob = useMemo(
		() =>
			wizard.activeJobs.find((job) => job.type === MediaJobType.NARRATION_TTS && job.step === 3),
		[wizard.activeJobs]
	);
	const isGenerating = !!scriptJob;

	// The server's own approve precondition, read here so the buttons say what is possible.
	const hasScript = !!scenes.length && scenes.every((scene) => !!scene.narrationText);
	const reports = scenes.filter((scene) => !!scene.narrationValidation);
	// A failing report gates the approval like step 1's length gate: a text the checks
	// refused will not fit its video, and approving it only pays the voice to prove that.
	const hasFailingChecks = reports.some((scene) => scene.narrationValidation?.passed === false);

	return (
		<div className="space-y-4">
			<header className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h2 className="text-lg font-bold text-ink">{ts("steps.narration.heading")}</h2>
					<p className="mt-1 max-w-[640px] text-xs text-subtle">
						{ts("steps.narration.description")}
					</p>
				</div>
				<span
					className={classNames(
						"shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold",
						approved
							? "bg-success-light text-text"
							: isGenerating
								? "bg-status-running-light text-warning-ink"
								: "bg-surface text-subtle"
					)}
				>
					{approved
						? ts("steps.narration.approvedBadge")
						: isGenerating
							? ts("steps.narration.generatingBadge")
							: ts("status.awaitingApproval")}
				</span>
			</header>

			<ModelSelectRow
				disabled={isBusy || isGenerating}
				step={3}
				useCase={AiModelUseCase.STORYBOARD}
				wizard={wizard}
				onSelectModel={onSelectModel}
			/>

			{!!reports.length && (
				<section className="space-y-2 rounded-card border border-border-soft bg-white p-4">
					<span className="block text-sm font-bold text-ink">
						{ts("steps.narration.governanceHeading")}
					</span>
					<p className="text-xs text-subtle">{ts("steps.narration.governanceHint")}</p>

					<div className="space-y-2">
						{reports.map((scene, index) => (
							<NarrationReport
								key={scene.id}
								disabled={isBusy || isGenerating}
								index={index}
								text={scene.narrationText ?? ""}
								title={scene.title}
								validation={scene.narrationValidation as NarrationValidation}
								onEdit={(text) => onEditSceneText(scene.id, text)}
								onRegenerate={(feedback) => onGenerate(feedback, undefined, [scene.id])}
							/>
						))}
					</div>
				</section>
			)}

			<section className="space-y-3 rounded-card border border-border-soft bg-surface p-4">
				<span className="text-sm font-bold text-ink">{ts("steps.narration.iterateHeading")}</span>

				{/* The standing brief — what kind of script it should be. Always on offer,
				    first draft included, and it applies to every generation until changed. */}
				<div>
					<span className="block text-xs font-bold text-ink">
						{ts("steps.narration.briefLabel")}
					</span>
					<p className="mb-1.5 mt-0.5 text-xs text-subtle">{ts("steps.narration.briefHint")}</p>
					<TextArea
						showError
						className="!min-h-[72px] text-sm"
						handleChange={(event) => setBrief(event.target.value)}
						name="wizard-script-brief"
						placeholder={ts("steps.narration.briefPlaceholder")}
						value={brief}
					/>
				</div>

				{/* Rewriting takes the author's notes to the script model — the same feedback
				    loop the other steps have. Only offered once there is a draft to react to. */}
				{hasScript && !isGenerating && (
					<TextArea
						showError
						className="!min-h-[72px] text-sm"
						handleChange={(event) => setFeedback(event.target.value)}
						name="wizard-script-feedback"
						placeholder={ts("steps.narration.feedbackPlaceholder")}
						value={feedback}
					/>
				)}

				<div className="flex flex-wrap gap-2">
					<Button
						color={hasScript ? ButtonColor.ACTION_SECONDARY : ButtonColor.ACTION}
						disabled={isBusy || isGenerating}
						size={ButtonSize.M}
						title={
							isGenerating
								? ts("steps.narration.generating")
								: hasScript
									? ts("steps.narration.regenerate")
									: ts("steps.narration.generate")
						}
						type="button"
						onClick={() => {
							// The brief is sent even when empty — an emptied box clears the
							// stored one. Feedback is one take's note and is spent with it.
							onGenerate(feedback.trim() || undefined, brief.trim());
							setFeedback("");
						}}
					/>
					{hasScript && !isGenerating && (
						<Button
							color={ButtonColor.ACTION}
							disabled={isBusy || hasFailingChecks}
							size={ButtonSize.M}
							title={ts("steps.narration.approve")}
							type="button"
							onClick={onApprove}
						/>
					)}
				</div>

				{hasFailingChecks && !isGenerating && (
					<p className="text-xs text-subtle">{ts("steps.narration.approveBlockedChecks")}</p>
				)}
				{!hasScript && !isGenerating && (
					<p className="text-xs text-subtle">{ts("steps.narration.generateHint")}</p>
				)}
				{isGenerating && (
					<p className="text-xs text-subtle">{ts("steps.narration.generatingHint")}</p>
				)}
			</section>
		</div>
	);
};

interface NarrationReportProps {
	index: number;
	title: string;
	text: string;
	validation: NarrationValidation;
	disabled?: boolean;
	/** Regenerates this scene's text alone, with the card's own feedback. */
	onRegenerate: (feedback?: string) => void;
	/** Replaces this scene's text with the author's own words. */
	onEdit: (text: string) => void;
}

/**
 * One scene's text and what the pipeline made of it. The failing rules are named, because
 * "geprüft" without a report is a claim the author cannot check. The card carries its own
 * iterate — a rewrite of this scene with its own feedback — and a manual edit whose words
 * are checked but never refused.
 */
const NarrationReport: FunctionComponent<NarrationReportProps> = ({
	index,
	title,
	text,
	validation,
	disabled,
	onRegenerate,
	onEdit,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`videoWizard.${key}`, options ?? {}),
		[t]
	);

	const [isEditing, setEditing] = useState(false);
	const [draft, setDraft] = useState(text);
	const [feedback, setFeedback] = useState("");

	const failed = validation.rules.filter((rule) => !rule.passed);

	return (
		<div className="rounded-card border border-border-soft bg-surface p-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<span className="text-xs font-bold text-ink">
					{ts("steps.narration.sceneNumber", { number: index + 1, title })}
				</span>
				<span
					className={classNames(
						"flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
						validation.passed ? "bg-success-light text-text" : "bg-error-light text-text"
					)}
				>
					<Icon icon={validation.passed ? faCheck : faTriangleExclamation} />
					{validation.passed
						? ts("steps.narration.checkPassed")
						: ts("steps.narration.checkFailed")}
				</span>
			</div>

			{isEditing ? (
				<div className="mt-2 space-y-2">
					<TextArea
						showError
						className="!min-h-[96px] text-sm"
						handleChange={(event) => setDraft(event.target.value)}
						name={`wizard-scene-script-${index}`}
						value={draft}
					/>
					<div className="flex gap-2">
						<Button
							color={ButtonColor.ACTION}
							disabled={disabled || !draft.trim()}
							size={ButtonSize.S}
							title={ts("steps.narration.saveText")}
							type="button"
							onClick={() => {
								onEdit(draft.trim());
								setEditing(false);
							}}
						/>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.S}
							title={ts("steps.narration.cancelEdit")}
							type="button"
							onClick={() => {
								setDraft(text);
								setEditing(false);
							}}
						/>
					</div>
				</div>
			) : (
				!!text && <p className="mt-2 text-xs text-text">{text}</p>
			)}

			<div className="mt-2 flex flex-wrap gap-2">
				<span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-subtle">
					{ts("steps.narration.wordCount", {
						words: validation.wordCount,
						target: validation.targetWordCount,
					})}
				</span>
				{validation.repairAttempts > 0 && (
					<span className="rounded-full bg-status-running-light px-2.5 py-1 text-xs font-semibold text-warning-ink">
						{ts("steps.narration.repaired", { times: validation.repairAttempts })}
					</span>
				)}
				{failed.map((rule) => (
					<span
						key={rule.rule}
						className="rounded-full bg-error-light px-2.5 py-1 text-xs font-semibold text-text"
						title={rule.detail ?? undefined}
					>
						{ts(`steps.narration.rules.${rule.rule}`)}
					</span>
				))}
			</div>

			{/* This scene's own iterate: a rewrite with the card's feedback, or the author's
			    words typed in directly — either costs the spoken take, never the other scenes.
			    A one-line input, like the scene cards' — the note is short, the text is not. */}
			{!isEditing && (
				<div className="mt-2 space-y-2 border-t border-border-soft pt-2">
					<TextArea
						showError
						className="!min-h-[60px] text-xs"
						disabled={disabled}
						handleChange={(event) => setFeedback(event.target.value)}
						name={`wizard-scene-script-feedback-${index}`}
						placeholder={ts("steps.narration.sceneFeedbackPlaceholder")}
						value={feedback}
					/>
					{/* Under the box, as one unit — rewrite and edit always share the row.
					    The rewrite needs the note above it — without one it would only reroll
					    the same dice, and the step-level regenerate already does that. */}
					<span className="flex shrink-0 gap-2">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={disabled || !feedback.trim()}
							image={<Icon className="mr-2" icon={faRotate} />}
							size={ButtonSize.S}
							title={ts("steps.narration.regenerateScene")}
							type="button"
							onClick={() => {
								onRegenerate(feedback.trim());
								setFeedback("");
							}}
						/>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={disabled}
							image={<Icon className="mr-2" icon={faPen} />}
							size={ButtonSize.S}
							title={ts("steps.narration.editText")}
							type="button"
							onClick={() => {
								setDraft(text);
								setEditing(true);
							}}
						/>
					</span>
				</div>
			)}
		</div>
	);
};
